<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="content">
					<app-heading :title="integration.name" />
					<app-section>
						<app-row>
							<app-cell>
								<app-json :data="integration.content" open />
							</app-cell>
						</app-row>
					</app-section>
				</app-tab-item>
				<app-tab-item :tab="tab" value="channels">
					<app-heading :title="`${integration.name} Channels`" />
					<app-data-table
						:items="resorts.items"
						:total="resorts.count"
						:headers="resorts.headers"
						@update="fetchResorts"
					>
						<template v-slot:channels="{ item }">
							<input-combobox
								v-if="resorts.channels[item._id]"
								:items="resorts.channels[item._id]"
								:value="resortChannels(item._id)"
								placeholder="None"
								multiple
								clearable
								@change="onResortChannelChange(item._id, $event)"
							></input-combobox>
						</template>
					</app-data-table>
				</app-tab-item>
				<app-tab-item :tab="tab" value="profiles">
					<app-heading :title="`${integration.name} Connection Profiles`">
						<template v-slot:left>
							<app-button v-if="$ravello.can('integration.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								small
								outlined
								:to="{ name: 'integration-profile', params: {
									integration: id,
									profile: 'new',
								}}"
							>Add Profile</app-button>
						</template>
					</app-heading>
					<app-data-table
						:items="profiles.items"
						:total="profiles.count"
						:headers="profiles.headers"
						@update="fetchProfiles"
					>
						<template v-slot:actions="{ item }">
							<app-button
								v-if="$ravello.can('integration.put')"
								color="secondary"
								:to="{ name: 'integration-profile', params: {
									integration: id,
									profile: item._id,
								}}"
								icon="fa-pencil-alt"
								table
							>
								Edit
							</app-button>
							<app-button-confirm
								v-if="$ravello.can('integration.delete')"
								color="error"
								icon="fa-trash"
								table
								label-confirm="Remove"
								:description="`Are you sure you want to remove ` +
									`profile <b>${item.name}</b>?`"
								title="Remove Profile"
								@click="onProfileDelete(item._id)"
							>
								Remove
							</app-button-confirm>
						</template>
					</app-data-table>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: 'integration-profile', params: {
							integration: id,
							profile: 'new',
						}}"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
				<app-tab-item :tab="tab" value="connections">
					<app-heading :title="`${integration.name} Connections`">
						<template v-slot:left>
							<app-button v-if="$ravello.can('integration.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								small
								outlined
								:to="{ name: 'integration-connection', params: {
									integration: id,
									connection: 'new',
								}}"
							>Add Connection</app-button>
						</template>
					</app-heading>
					<app-data-table
						:items="connections.items"
						:total="connections.count"
						:headers="connections.headers"
						@update="fetchConnections"
					>
						<template v-slot:channels="{ item }">
							<input-combobox
								v-if="integration.channels"
								:items="connectionChannelsOptions()"
								:value="item.channels"
								placeholder="None"
								multiple
								clearable
								@change="onConnectionChannelChange(item._id, $event)"
							></input-combobox>
						</template>
						<template v-slot:actions="{ item }">
							<app-button
								v-if="$ravello.can('integration.put')"
								color="secondary"
								:to="{ name: 'integration-connection', params: {
									integration: id,
									connection: item._id,
								}}"
								icon="fa-pencil-alt"
								table
							>
								Edit
							</app-button>
							<app-button
								color="secondary"
								icon="fa-sync"
								@click="syncConnection(item._id)"
								v-if="$ravello.can('system.post')"
								table
							>
								Sync
							</app-button>
							<app-button
								color="secondary"
								icon="fa-paper-plane"
								@click="sendConnectionToStudeo(item._id)"
								v-if="$ravello.can('integration.post')"
								table
							>
								Send To Studeo
							</app-button>
							<app-button-confirm
								v-if="$ravello.can('integration.delete')"
								color="error"
								icon="fa-trash"
								table
								label-confirm="Remove"
								:description="`Are you sure you want to remove ` +
									`connection <b>${item.name}</b>?`"
								title="Remove Connection"
								@click="onConnectionDelete(item._id)"
							>
								Remove
							</app-button-confirm>
						</template>
					</app-data-table>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: 'integration-connection', params: {
							integration: id,
							connection: 'new',
						}}"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
				<app-tab-item :tab="tab" value="mapping">
					<app-heading :title="`${integration.name} Mapping`">
						<template v-slot:right>
							<app-button
								color="secondary"
								small
								outlined
								icon="fa-file-download"
								@click="onExport"
							>Export</app-button>
							<input-file
								type="json"
								button
								small
								outlined
								label="Import"
								@load="onImport"
							/>
						</template>
					</app-heading>
					<app-tabs :value="mapTab" :tabs="mapTabs" @change="onMapTabChange">
						<template v-slot:tab-item="{ tab }">
							<app-tab-item :tab="tab" value="property-types">
								<app-data-table
									:items="propertyTypes.items"
									:total="propertyTypes.count"
									:headers="propertyTypes.headers"
									@update="fetchMapData('propertyTypes', $event)"
								>
									<template v-slot:map="{ item }">
										<input-combobox
											v-if="integration.maps.property_types"
											:items="integrationOptions('property_types')"
											:value="integrationOptionValue('property_types', item._id)"
											placeholder="None"
											clearable
											@change="onMapChange('property_types', item._id, $event)"
										></input-combobox>
									</template>
								</app-data-table>
							</app-tab-item>
							<app-tab-item :tab="tab" value="room-types">
								<app-data-table
									:items="roomTypes.items"
									:total="roomTypes.count"
									:headers="roomTypes.headers"
									@update="fetchMapData('roomTypes', $event)"
								>
									<template v-slot:map="{ item }">
										<input-combobox
											v-if="integration.maps.room_types"
											:items="integrationOptions('room_types')"
											:value="integrationOptionValue('room_types', item._id)"
											placeholder="None"
											clearable
											@change="onMapChange('room_types', item._id, $event)"
										></input-combobox>
									</template>
								</app-data-table>
							</app-tab-item>
							<app-tab-item :tab="tab" value="bed-types">
								<app-data-table
									:items="bedTypes.items"
									:total="bedTypes.count"
									:headers="bedTypes.headers"
									@update="fetchMapData('bedTypes', $event)"
								>
									<template v-slot:map="{ item }">
										<input-combobox
											v-if="integration.maps.bed_types"
											:items="integrationOptions('bed_types')"
											:value="integrationOptionValue('bed_types', item._id)"
											placeholder="None"
											clearable
											@change="onMapChange('bed_types', item._id, $event)"
										></input-combobox>
									</template>
								</app-data-table>
							</app-tab-item>
							<app-tab-item :tab="tab" value="features">
								<app-data-table
									:items="features.items"
									:total="features.count"
									:headers="features.headers"
									search
									@search="onMapSearch('features', $event)"
									@update="fetchMapData('features', $event)"
								>
									<template v-slot:map="{ item }">
										<input-combobox
											v-if="integration.maps.features"
											:items="integrationOptions('features')"
											:value="integrationOptionValue('features', item._id)"
											placeholder="None"
											clearable
											@change="onMapChange('features', item._id, $event)"
										></input-combobox>
									</template>
								</app-data-table>
							</app-tab-item>
							<app-tab-item :tab="tab" value="amenities">
								<app-data-table
									:items="amenities.items"
									:total="amenities.count"
									:headers="amenities.headers"
									search
									@search="onMapSearch('amenities', $event)"
									@update="fetchMapData('amenities', $event)"
								>
									<template v-slot:map="{ item }">
										<input-combobox
											v-if="integration.maps.amenities"
											:items="integrationOptions('features')"
											:value="integrationOptionValue('amenities', item._id)"
											placeholder="None"
											clearable
											@change="onMapChange('amenities', item._id, $event)"
										></input-combobox>
									</template>
								</app-data-table>
							</app-tab-item>
						</template>
					</app-tabs>
				</app-tab-item>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { mapKeys } from 'lodash'
import { saveAs } from 'file-saver'

import { mapSelectOptions, mapEntries, mapFields } from '@/utils/maps'
import { integrations as integrationTabs } from '@/datasources/tabs'

import Tab from '@/types/Tab'

import { reduceAsync, mapAll } from '@/utils/async'

const IMPORT_LIMIT = 25

function MapType() {
	this.items = []
	this.headers = [{
		text: 'ID',
		value: 'studeo_id',
		sortable: true,
	}, {
		text: 'Name',
		value: 'name',
		sortable: true,
	}, {
		text: 'Map',
		value: 'map',
		sortable: false,
	}]
	this.searchText = null
	this.count = -1
}

function ResortChannelType() {
	this.items = []
	this.headers = [{
		text: 'ID',
		value: 'studeo_id',
		sortable: true,
	}, {
		text: 'Name',
		value: 'name',
		sortable: true,
	}, {
		text: 'Channels',
		value: 'channels',
		sortable: false,
	}]
	this.count = -1
	this.channels = {}
}

function ConnectionsType() {
	this.items = []
	this.headers = [{
		text: 'Name',
		value: 'name',
		sortable: true,
	}, {
		text: 'Enterprise',
		value: 'enterprise.name',
		sortable: false,
	}, {
		text: 'Profile',
		value: 'profile.name',
		sortable: false,
	}, {
		text: 'Channels',
		value: 'channels',
		sortable: false,
	}, {
		text: '',
		align: 'end',
		value: 'actions',
		sortable: false,
	}]
	this.count = -1
}

function ProfilesType() {
	this.items = []
	this.headers = [{
		text: 'Name',
		value: 'name',
		sortable: true,
	}, {
		text: 'Slug',
		value: 'slug',
		sortable: true,
	}, {
		text: '',
		align: 'end',
		value: 'actions',
		sortable: false,
	}]
	this.count = -1
}

export default {
	async created() {
		this.$store.dispatch('app/startLoading')
		try {
			this.channelsMap = mapEntries(await this.$ravello.request()
				.v(2)
				.to('channels')
				.limit(1000)
				.get())

			this.resortsMap = mapFields(await this.$ravello.request()
				.v(2)
				.to('resorts')
				.limit(1000)
				.get(), 'name')

			const integration = await this.$ravello.request()
				.v(2)
				.to('integrations')
				.to(this.id)
				.get()

			this.integration = Object.keys(this.integration).reduce((acc, field) => {
				acc[field] = integration[field]
				return acc
			}, {})
		} catch (err) {
			this.$notifications.pushAlert('error', err.data.message)
		}

		this.tabs = integrationTabs(this.id)
		this.$store.dispatch('app/stopLoading')
	},
	data() {
		return {
			integration: {
				name: '',
				slug: '',
				maps: {},
				enums: {},
				channels: [],
				content: {},
			},
			tabs: [],
			mapTabs: [
				new Tab('property-types', 'Property types'),
				new Tab('room-types', 'Room types'),
				new Tab('bed-types', 'Bed types'),
				new Tab('features', 'Features'),
				new Tab('amenities', 'Amenities'),
			],
			propertyTypes: new MapType(),
			bedTypes: new MapType(),
			roomTypes: new MapType(),
			features: new MapType(),
			amenities: new MapType(),
			resorts: new ResortChannelType(),
			connections: new ConnectionsType(),
			profiles: new ProfilesType(),
			mapTab: null,
			errors: {},
			channelsMap: {},
			resortsMap: {},
			mapEndoints: {
				property_types: 'propertytypes',
				room_types: 'roomtypes',
				bed_types: 'bedtypes',
				features: 'features',
				amenities: 'amenities',
			},
		}
	},
	computed: {
		id() {
			return this.$route.params.integration
		},
	},
	methods: {
		onMapSearch(type, searchText) {
			this[type].searchText = searchText
			this[type].count = -1
		},
		async fetchMapData(type, pagination) {
			const endpoint = this.mapEndoints[type] || type.toLowerCase()
			const request = this.$ravello.request()
				.v(2)
				.to(endpoint)
				.fromPagination(pagination)

			if (this[type].searchText) {
				request.param('search', this[type].searchText)
			}

			try {
				const items = await request.get()
				// Extract name from translations.en for amenities
				if (type === 'amenities') {
					this[type].items = items.map(item => ({
						...item,
						name: this.getTranslation(item.translations, 'en'),
					}))
				} else {
					this[type].items = items
				}

				if (this[type].searchText) {
					this[type].count = this[type].items.length
				}
				if (this[type].count === -1) {
					const count = await request.count()
					this[type].count = count.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		connectionChannelsOptions() {
			return mapSelectOptions(this.integration.channels.reduce((acc, ic) => {
				if (this.channelsMap[ic.channel]) {
					acc.push({
						...this.channelsMap[ic.channel],
						group: this.resortsMap[this.channelsMap[ic.channel].resort],
					})
				}
				return acc
			}, []), { group: 'group' })
		},

		resortChannels(id) {
			return this.integration.channels.reduce((acc, rc) => {
				if (rc.resort === id) {
					acc.push(rc.channel)
				}
				return acc
			}, [])
		},

		integrationOptions(type) {
			if (!this.integration || !this.integration.enums[type]) {
				return []
			}

			return this.integration.enums[type].map((data) => {
				if (typeof data !== 'object') {
					return data
				}

				return {
					text: data.text,
					value: JSON.stringify(data),
				}
			}).sort((a, b) => {
				if (a.text) {
					return a.text.localeCompare(b.text)
				}

				return a.localeCompare(b)
			})
		},

		integrationOptionValue(type, id) {
			if (!this.integration || !this.integration.maps[type] || !this.integration.maps[type][id]) {
				return null
			}

			const value = this.integration.maps[type][id]
			if (typeof value !== 'object') {
				return value
			}

			return JSON.stringify(value)
		},

		async fetchResorts(pagination) {
			const request = this.$ravello.request()
				.v(2)
				.to('resorts')
				.fromPagination(pagination)

			try {
				this.resorts.items = await request.get()
				if (this.resorts.count === -1) {
					const count = await request.count()
					this.resorts.count = count.count
				}

				const resortChannels = await Promise.all(this.resorts.items.map(async (resort) => {
					const channels = await this.$ravello.request()
						.v(2)
						.to('resorts')
						.to(resort._id)
						.to('channels')
						.limit(1000)
						.get()
					return channels
				}))

				this.resorts.channels = this.resorts.items.reduce((acc, resort, i) => {
					acc[resort._id] = mapSelectOptions(resortChannels[i])
					return acc
				}, {})
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async fetchProfiles(pagination) {
			const request = this.$ravello.graphql('profiles')
				.parent(this.$ravello.graphql('integration', this.id))
				.fields(['_id', 'name', 'slug'])
				.fromPagination(pagination)

			if (this.profiles.count === -1) {
				request.count('count')
			}

			try {
				this.profiles.items = await request.query()
				if (this.profiles.count === -1) {
					this.profiles.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async fetchConnections(pagination) {
			const request = this.$ravello.graphql('connections')
				.parent(this.$ravello.graphql('integration', this.id))
				.fields(['_id', 'name', 'channels'])
				.include(this.$ravello.graphql('enterprise').field('name'))
				.include(this.$ravello.graphql('profile').field('name'))
				.fromPagination(pagination)

			if (this.connections.count === -1) {
				request.count('count')
			}

			try {
				this.connections.items = await request.query()
				if (this.connections.count === -1) {
					this.connections.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onMapChange(field, id, value) {
			this.$store.dispatch('app/startLoading')

			if (value && value !== '') {
				if (value.substr(0, 1) === '{' || value.substr(0, 1) === '[') {
					this.integration.maps[field][id] = JSON.parse(value)
				} else {
					this.integration.maps[field][id] = value
				}
			} else {
				delete this.integration.maps[field][id]
			}

			try {
				await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.id)
					.update({
						[`maps.${field}`]: this.integration.maps[field],
					})
				this.$notifications.pushAlert('success', 'Integration mapping successfully updated')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		onMapTabChange(tab) {
			this.mapTab = tab
		},

		async onResortChannelChange(id, channels) {
			const channelsMap = this.integration.channels.reduce((acc, ic) => {
				if (ic.resort !== id
					|| (ic.resort === id && channels.find(channel => channel === ic.channel))) {
					acc[`${ic.resort}:${ic.channel}`] = ic
				}
				return acc
			}, {})
			channels.forEach((channel) => {
				if (!channelsMap[`${id}:${channel}`]) {
					channelsMap[`${id}:${channel}`] = {
						resort: id,
						channel,
					}
				}
			})
			this.integration.channels = Object.values(channelsMap)

			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.id)
					.update({
						channels: this.integration.channels,
					})
				this.$notifications.pushAlert('success', 'Integration channels successfully updated')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onConnectionChannelChange(id, channels) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.id)
					.to('connections')
					.to(id)
					.update({ channels })

				this.$notifications.pushAlert('success', 'Connection channels successfully updated')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async sendConnectionToStudeo(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.id)
					.to('connections')
					.to(id)
					.to('studeify')
					.create()

				this.$notifications.pushAlert('success', 'Connection pushed to Studeo')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async syncConnection(id) {
			this.$store.dispatch('app/startLoading')

			try {
				await this.$ravello.request()
					.to('system')
					.to('events')
					.to('trigger')
					.create({
						event: `v2.${this.integration.slug}.sync.connection`,
						data: {
							id,
						},
					})
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}

			this.$store.dispatch('app/stopLoading')
		},

		async onProfileDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.id)
					.to('profiles')
					.to(id)
					.delete()

				this.$notifications.pushAlert('success', 'Profile successfully deleted')
				this.profiles.count = -1
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onConnectionDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.id)
					.to('connections')
					.to(id)
					.delete()

				this.$notifications.pushAlert('success', 'Connection successfully deleted')
				this.connections.count = -1
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onExport() {
			this.$store.dispatch('app/startLoading')
			const exportData = await reduceAsync(this.integration.maps, async (acc, map, key) => {
				const ids = Object.keys(map)
				const iterations = Array.from(Array(Math.ceil(ids.length / IMPORT_LIMIT)).keys())

				const data = await mapAll(iterations, i => this.$ravello.request()
					.v(2)
					.to(this.mapEndoints[key])
					.param('_id', ids.slice(i * IMPORT_LIMIT, (i + 1) * IMPORT_LIMIT))
					.fields(['studeo_id'])
					.get())

				const studeoMap = mapFields(data.reduce((acc2, chunk) => [...acc2, ...chunk], []), 'studeo_id')

				return { ...acc, [key]: mapKeys(map, (content, id) => studeoMap[id]) }
			}, {})

			const blob = new Blob([JSON.stringify(exportData)], { type: 'application/json;charset=utf-8' })
			saveAs(blob, `${this.integration.slug}_map.json`)
			this.$store.dispatch('app/stopLoading')
		},

		getTranslation(translations, lang = 'en') {
			if (!translations) return ''
			if (typeof translations === 'string') return translations
			if (translations[lang]) return translations[lang]
			// Fallback to first available translation
			const keys = Object.keys(translations)
			return keys.length > 0 ? translations[keys[0]] : ''
		},
		reloadMapTab() {
			switch (this.mapTab) {
			case 'room-types':
				this.roomTypes.count = -1
				break
			case 'bed-types':
				this.bedTypes.count = -1
				break
			case 'features':
				this.features.count = -1
				break
			case 'amenities':
				this.amenities.count = -1
				break
			default:
				this.propertyTypes.count = -1
				break
			}
		},
		async onImport(importData) {
			this.$store.dispatch('app/startLoading')

			const requiredMaps = Object.keys(this.mapEndoints)
			const valid = Object.keys(importData).every((key) => {
				const found = requiredMaps.indexOf(key)
				if (found === -1) { return false }
				requiredMaps.splice(found, 1)
				return true
			})

			if (!valid || requiredMaps.length > 0) {
				this.$notifications.pushAlert('error', 'Imported JSON is an invalid or an old map version')
				return
			}

			const maps = await reduceAsync(importData, async (acc, map, key) => {
				const ids = Object.keys(map)

				const iterations = Array.from(Array(Math.ceil(ids.length / IMPORT_LIMIT)).keys())
				const hubData = await mapAll(iterations, i => this.$ravello.request()
					.v(2)
					.to(this.mapEndoints[key])
					.param('studeo_id', ids.slice(i * IMPORT_LIMIT, (i + 1) * IMPORT_LIMIT))
					.fields(['studeo_id'])
					.get())

				const hubMap = mapFields(
					hubData.reduce((acc2, chunk) => [...acc2, ...chunk], []),
					'_id', { key: 'studeo_id' },
				)

				const newMap = mapKeys(map, (content, id) => hubMap[id])
				delete newMap[undefined] // Remove any not found map
				return { ...acc, [key]: newMap }
			}, {})

			this.integration.maps = maps

			try {
				await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.id)
					.update({
						maps: this.integration.maps,
					})
				this.$notifications.pushAlert('success', 'Integration mapping successfully imported')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.reloadMapTab()
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>
